import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/**
 * WE2 P3a-02: TapePanel (moved from src/lib/tape/TapePanel.tsx)
 *
 * Pure-presentational Time-and-Sales (tape) panel.
 * Data is fetched externally (demo shell) and passed via props.
 * All text goes through useWidgetI18n().t() — no hardcoded Vietnamese.
 *
 * Layout: vertical scrollable list — each row is 22px:
 *   [time] [price] [volume] [value] [side badge]
 * Whale rows get a yellow background.
 * Auto-scrolls to the bottom when new entries arrive.
 */
import { useEffect, useRef } from "react";
import { useWidgetI18n } from "../../context/WidgetI18nContext";
import styles from "../../styles/tape-panel.module.css";
// ── Constants ─────────────────────────────────────────────────────────────────
const ROW_HEIGHT = 22;
const WHALE_BG = "#2a2300"; // dark yellow tint for whale rows
const WHALE_BORDER = "#b8860b";
// ── Helpers ───────────────────────────────────────────────────────────────────
function formatTime(iso) {
    try {
        const d = new Date(iso);
        const h = d.getHours().toString().padStart(2, "0");
        const m = d.getMinutes().toString().padStart(2, "0");
        const s = d.getSeconds().toString().padStart(2, "0");
        return `${h}:${m}:${s}`;
    }
    catch {
        return iso.slice(11, 19) || "--:--:--";
    }
}
function formatPrice(p) {
    return p.toLocaleString("vi-VN");
}
function formatVolume(v) {
    if (v >= 1_000_000)
        return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000)
        return `${(v / 1_000).toFixed(0)}K`;
    return v.toString();
}
function formatValue(v, t) {
    const billions = v / 1_000_000_000;
    if (billions >= 1)
        return `${billions.toFixed(1)}${t("tape.billions")}`;
    const millions = v / 1_000_000;
    if (millions >= 1)
        return `${millions.toFixed(0)}tr`;
    return v.toString();
}
export function TapePanel({ show, entries, isLoading, symbol, filterBillions, onFilterChange, onClose, }) {
    const { t } = useWidgetI18n();
    const scrollRef = useRef(null);
    const prevLengthRef = useRef(0);
    const userScrolledRef = useRef(false);
    const SIDE_COLORS = {
        buy: { bg: "#0d6b2e", text: "#ffffff", label: t("tape.sideBuy") ?? "Buy" },
        sell: { bg: "#c0392b", text: "#ffffff", label: t("tape.sideSell") ?? "Sell" },
        unknown: { bg: "#4a4a4a", text: "#aaaaaa", label: t("tape.sideUnknown") ?? "Unknown" },
    };
    // Auto-scroll to bottom only when new entries arrive and user hasn't manually scrolled up
    useEffect(() => {
        if (!show)
            return;
        if (entries.length > prevLengthRef.current) {
            prevLengthRef.current = entries.length;
            if (!userScrolledRef.current) {
                const el = scrollRef.current;
                if (el)
                    el.scrollTop = el.scrollHeight;
            }
        }
    }, [entries.length, show]);
    if (!show)
        return null;
    const threshold = filterBillions * 1_000_000_000;
    const filtered = threshold > 0
        ? entries.filter((e) => e.value >= threshold)
        : entries;
    return (_jsxs("div", { className: styles.panel, "data-testid": "tape-panel", children: [_jsxs("div", { className: styles.header, children: [_jsxs("span", { className: styles.title, children: [t("tape.title"), symbol ? ` — ${symbol}` : ""] }), onClose ? (_jsx("button", { onClick: onClose, title: t("tape.close"), className: styles.closeBtn, children: "\u00D7" })) : null] }), _jsxs("div", { className: styles.filterBar, children: [_jsx("span", { className: styles.filterLabel, children: "\u2265" }), _jsx("input", { type: "range", min: 0, max: 50, step: 0.5, value: filterBillions, onChange: (e) => onFilterChange?.(Number(e.target.value)), className: styles.filterSlider, "aria-label": t("tape.filterLabel"), title: `≥ ${filterBillions} ${t("tape.billions")}` }), _jsx("span", { className: styles.filterValue, "data-testid": "tape-filter-label", children: filterBillions > 0 ? `${filterBillions}${t("tape.billions")}` : t("tape.all") })] }), _jsxs("div", { className: styles.columnHeaders, children: [_jsx("span", { children: t("tape.colTime") }), _jsx("span", { style: { textAlign: "right" }, children: t("tape.colPrice") }), _jsx("span", { style: { textAlign: "right" }, children: t("tape.colVolume") }), _jsx("span", { style: { textAlign: "right" }, children: t("tape.colValue") }), _jsx("span", { style: { textAlign: "center" } })] }), _jsx("div", { ref: scrollRef, className: styles.entriesList, onScroll: () => {
                    const el = scrollRef.current;
                    if (!el)
                        return;
                    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 10;
                    userScrolledRef.current = !atBottom;
                }, children: isLoading && filtered.length === 0 ? (_jsx("div", { className: styles.loading, "data-testid": "tape-loading", children: t("tape.loading") })) : filtered.length === 0 ? (_jsx("div", { className: styles.empty, "data-testid": "tape-empty", children: t("tape.empty") })) : (filtered.map((entry) => {
                    const sideStyle = SIDE_COLORS[entry.side] ?? SIDE_COLORS.unknown;
                    return (_jsxs("div", { "data-testid": "tape-row", className: `${styles.row} ${entry.is_whale ? styles.rowWhale : ""}`, children: [_jsx("span", { className: styles.rowTime, children: formatTime(entry.ts) }), _jsx("span", { className: `${styles.rowPrice} ${entry.side === "buy" ? styles.rowPriceBuy : entry.side === "sell" ? styles.rowPriceSell : ""} ${entry.is_whale ? styles.rowPriceWhale : ""}`, children: formatPrice(entry.price) }), _jsx("span", { className: styles.rowVolume, children: formatVolume(entry.volume) }), _jsx("span", { className: `${styles.rowValue} ${entry.is_whale ? styles.rowValueWhale : ""}`, children: formatValue(entry.value, (key) => t(key) ?? '') }), _jsx("span", { className: `${styles.rowSide} ${styles[`rowSide${entry.side.charAt(0).toUpperCase() + entry.side.slice(1)}`]}`, "data-side": entry.side, children: sideStyle.label })] }, entry.id));
                })) }), _jsxs("div", { className: styles.footer, children: [filtered.length, " ", t("tape.orderCount"), filterBillions > 0 ? ` (≥${filterBillions}${t("tape.billions")})` : ""] })] }));
}
